import { Pto } from '@outcoreaix/pto'
import { apiSlice } from './api-splice'
import { getQueryParamString } from 'utils/get-query-param-string'

export const appFlowsApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    createFlow: builder.mutation<Pto.AppFlows.Flow, Pto.AppFlows.CreateFlow>({
      query: (data) => ({
        url: 'app-flows',
        method: 'POST',
        body: data
      }),
      invalidatesTags: ['Flows']
    }),
    getFlows: builder.query<Pto.AppFlows.FlowsList, Pto.AppFlows.FlowsListQuery>({
      query: (params) => {
        const queryParams = getQueryParamString(params)
        return {
          url: `app-flows/?${queryParams}`,
          method: 'GET'
        }
      },
      providesTags: ['Flows']
    }),
    getFlow: builder.query<Pto.AppFlows.PopulatedFlowWithAnswers, { id: number }>({
      query: ({ id }) => ({
        url: `app-flows/${id}`,
        method: 'GET'
      }),
      providesTags: (_, __, { id }) => [{ type: 'Flows', id }]
    }),
    getDefaultFlow: builder.query<Pto.AppFlows.PopulatedFlow, void>({
      query: () => ({
        url: 'app-flows/default',
        method: 'GET'
      })
    }),
    updateFlowGeneralInfo: builder.mutation<void, { flowId: number } & Pto.AppFlows.UpdateFlowGeneralInfo>({
      query: ({ flowId, ...data }) => ({
        url: `app-flows/general/${flowId}`,
        method: 'PATCH',
        body: data
      }),
      invalidatesTags: ['Flows']
    }),
    addFlowQuestion: builder.mutation<void, { flowId: number } & Pto.AppFlows.CreateInductionQuestion>({
      query: ({ flowId, ...data }) => ({
        url: `app-flows/${flowId}/questions`,
        method: 'POST',
        body: data
      }),
      invalidatesTags: (_, __, { flowId }) => [{ type: 'Flows', flowId }]
    }),
    updateFlowQuestion: builder.mutation<
      void,
      { questionId: number; flowId: number } & Pto.AppFlows.UpdateInductionQuestion
    >({
      query: ({ questionId, flowId, ...data }) => ({
        url: `app-flows/questions/${questionId}`,
        method: 'PATCH',
        body: data
      }),
      invalidatesTags: (_, __, { flowId }) => [{ type: 'Flows', flowId }]
    }),
    deleteFlowQuestion: builder.mutation<void, { questionId: number; flowId: number }>({
      query: ({ questionId }) => ({
        url: `app-flows/questions/${questionId}`,
        method: 'DELETE'
      }),
      invalidatesTags: (_, __, { flowId }) => [{ type: 'Flows', flowId }]
    }),
    deleteFlow: builder.mutation<void, { id: number }>({
      query: ({ id }) => ({
        url: `app-flows/${id}`,
        method: 'DELETE'
      }),
      invalidatesTags: ['Flows', 'Starters']
    }),
    //categories
    getCategories: builder.query<Pto.AppFlows.QuestionCategory[], void>({
      query: () => ({
        url: 'app-flows/categories',
        method: 'GET'
      }),
      providesTags: ['InductionQuestionsCategories']
    }),
    getCategoriesWithFlows: builder.query<Pto.AppFlows.QuestionCategoryWithFlows[], void>({
      query: () => ({
        url: 'app-flows/categories-with-flows',
        method: 'GET'
      }),
      providesTags: ['InductionQuestionsCategories', 'Flows']
    }),
    createCategory: builder.mutation<Pto.AppFlows.QuestionCategory, Pto.AppFlows.CreateQuestionCategory>({
      query: (data) => ({
        url: 'app-flows/categories',
        method: 'POST',
        body: data
      }),
      invalidatesTags: ['InductionQuestionsCategories']
    }),
    updateCategory: builder.mutation<void, { categoryId: number } & Pto.AppFlows.UpdateQuestionCategory>({
      query: ({ categoryId, ...body }) => ({
        url: `app-flows/categories/${categoryId}`,
        method: 'PUT',
        body: body
      }),
      invalidatesTags: ['InductionQuestionsCategories']
    }),
    deleteCategory: builder.mutation<void, { categoryId: number }>({
      query: ({ categoryId }) => ({
        url: `app-flows/categories/${categoryId}`,
        method: 'DELETE'
      }),
      invalidatesTags: ['InductionQuestionsCategories']
    })
  })
})

export const {
  useCreateFlowMutation,
  useGetFlowsQuery,
  useGetFlowQuery,
  useGetDefaultFlowQuery,
  useUpdateFlowGeneralInfoMutation,
  useAddFlowQuestionMutation,
  useUpdateFlowQuestionMutation,
  useDeleteFlowQuestionMutation,
  useDeleteFlowMutation,

  useGetCategoriesQuery,
  useGetCategoriesWithFlowsQuery,
  useCreateCategoryMutation,
  useUpdateCategoryMutation,
  useDeleteCategoryMutation
} = appFlowsApi
